/* @flow */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import useSheet from 'app/jss';
import * as cssUtils from 'app/utils/cssUtils';
import classNames from 'classnames';
import colorUtils from 'color';
import { noop, get } from 'lodash';
import t from 'app/utils/translate';
import { connect } from 'react-redux';
import { createGroup } from 'app/actions/groupActions';
import { TweenMax, TimelineMax, Linear } from 'gsap';
import FriendTextInput from 'app/components/friends/common/FriendTextInput';
import FriendGroupActionPanel from './FriendGroupActionPanel';
import IconButton from 'app/components/commons/IconButton';
import guid from 'app/utils/guid';

type Props = {
  sheet: Object,
  friendId: string | number,
  savingGroups: Set<number | string>,
  savingGroupFailed: boolean,
  onGroupCreate: () => void,
  onCancel: () => void,
  onConfirm?: () => void,
};

type State = {
  groupName: string,
};

function shakeInput(ref) {
  const nameBox = ReactDOM.findDOMNode(ref);
  const timeline = new TimelineMax();
  // tween it to the left first in prep for the bouncing
  timeline.add(TweenMax.fromTo(nameBox, 0.05, { x: 0 }, { x: 5, ease: Linear.easeOut }));
  // then bounce it back and forth
  timeline.add(TweenMax.fromTo(nameBox, 0.1,
    { x: 5 },
    { x: -5, repeat: 3, yoyo: true, ease: Linear.easeOut },
  ));
  timeline.add(TweenMax.to(nameBox, 0.05, { x: 0, ease: Linear.easeOut, clearProps: 'x' }));
  timeline.play();
}

class FriendCreateGroup extends Component<Props, State> {
  props: Props;
  state: State = {
    groupName: '',
    isSaving: false,
  };

  newGroupId: string = guid();

  static defaultProps = {
    onConfirm: noop,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!this.props.savingGroupFailed && nextProps.savingGroupFailed) {
      // creating group failed, shake the input. Bad user!
      shakeInput(this.nameRef);
    } else if (this.props.savingGroups.has(this.newGroupId) &&
               !nextProps.savingGroups.has(this.newGroupId)) {
      // created succesfully, call on confirm
      this.props.onConfirm(nextProps.currentGroup);
    }
  }

  onTextChange = (value) => {
    this.setState({ groupName: value });
  }

  onConfirm = () => {
    // guard against creating a group if invalid, i.e. no name
    if (this.state.groupName) {
      const newGroup = {
        id: this.newGroupId,
        name: this.state.groupName,
        members: [
          this.props.friendId,
        ],
      };

      this.props.onGroupCreate(newGroup);
    } else {
      shakeInput(this.nameRef);
    }
  }

  render() {
    const {
      sheet: { classes },
      onCancel,
    } = this.props;
    const { groupName } = this.state;

    const isValid = !!groupName;
    return (
      <div className={classes.createGroup}>
        <div className={classes.groupForm}>
          <FriendTextInput
            ref={(ref) => { this.nameRef = ref; }}
            onTextChange={this.onTextChange}
            placeholder={t('GROUP NAME')}
            onSubmit={this.onConfirm}
            autoFocus
            focusDelay={301}
          />
        </div>
        <FriendGroupActionPanel className={classes.actionPanel}>
          <div className={classes.newGroupButtons}>
            <IconButton
              className={classNames(classes.button, classes.cancel)}
              onClick={onCancel}
              type="icon-cross-circle"
            />
            <IconButton
              className={classNames(classes.button, classes.create, { disabled: !isValid })}
              onClick={this.onConfirm}
              style={{ cursor: isValid ? 'pointer' : 'not-allowed' }}
              type={'icon-plus-circle'}
            />
          </div>
        </FriendGroupActionPanel>
      </div>
    );
  }
}


function applyStyles(theme: Theme) {
  return {
    createGroup: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    groupForm: {
      flexGrow: 1,
      justifyContent: 'center',
      padding: 10,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.color.basis2,
      fontSize: theme.fontSize.large,
    },
    newGroupButtons: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      '& > *:not(:last-child)': {
        marginRight: 25,
      },
    },
    actionPanel: {
      borderTopColor: theme.color.basis3,
      backgroundColor: colorUtils(theme.color.basis2).darken(0.1).hex(),
    },
    button: {
      fontSize: 25,
      transition: 'color 0.2s',
      cursor: 'pointer',
    },
    create: {
      color: theme.color.primary,
      '&.disabled': {
        cursor: 'not-allowed',
        color: theme.color.basis3,
      },
      '&:hover:not(.disabled)': {
        color: colorUtils(theme.color.primary).darken(0.1).hex(),
      },
    },
    cancel: {
      color: theme.color.basis6,
      ...cssUtils.hover({
        color: colorUtils(theme.color.basis6).darken(0.1).hex(),
      }),
    },
  };
}

function mapPropsToState(state: GlobalState, props: Props) {
  return {
    savingGroupFailed: state.friendGroups.savingGroupFailed,
    savingGroups: state.friendGroups.savingGroups,
    currentGroup: state.friendGroups.currentGroup,
  };
}

const actions = {
  onGroupCreate: createGroup,
};


export default connect(mapPropsToState, actions)(useSheet(applyStyles)(FriendCreateGroup));
